import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Menu as MenuIcon,
  Phone as PhoneIcon,
  People as PeopleIcon,
  ForumOutlined as ForumOutlinedIcon,
  Settings as SettingsIcon,
  AccountTree as AccountTreeIcon,
  Storage as StorageIcon,
  DnsOutlined as DnsOutlinedIcon,
  TableChartOutlined as TableChartOutlinedIcon,
  Campaign as CampaignIcon,
  InboxOutlined as InboxOutlinedIcon,
  Logout as LogoutIcon,
  CloudUpload as CloudUploadIcon,
  School as SchoolIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Brightness4 as MoonIcon,
  Brightness7 as SunIcon,
  Close as CloseIcon,
  AutoAwesome as AutoAwesomeIcon,
  SupportAgentOutlined as SupportAgentOutlinedIcon,
  DashboardOutlined as DashboardOutlinedIcon,
  ChatBubbleOutline as ChatBubbleOutlineIcon,
  TranslateRounded as TranslateRoundedIcon,
  Functions as FunctionsIcon,
  Api as ApiIcon,
  SendOutlined as SendOutlinedIcon,
  ContactsOutlined as ContactsIcon,
  QueryStatsOutlined as CampaignAnalyticsIcon,
  DescriptionOutlined as TemplateIcon,
  PersonSearchOutlined as Customer360Icon,
  VerifiedUserOutlined as ConsentIcon,
  HistoryOutlined as TimelineIcon,
} from '@mui/icons-material';
import { AnimatePresence, motion } from 'framer-motion';
import { Button, IconButton } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import EnvironmentSwitcher from './EnvironmentSwitcher';
import SidebarNav from './ui/SidebarNav';
import SaaSButton from './ui/SaaSButton';
import { useThemeMode } from '../context/ThemeModeContext';
import useFlowBuilderStore from '../store/flowBuilderStore';
import GlobalTicketNotification from './GlobalTicketNotification';

/** Purpose: provide the shared application shell, sidebar, header, and page layout. Why: all routes need consistent navigation and workspace context. */
export default function Layout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [navCollapsed, setNavCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, selectedBot, selectedBotDetails, botAccess, isAdmin, getEnabledServices } = useAuth();
  const { mode, toggleMode } = useThemeMode();
  const isFlowRoute = location.pathname.startsWith('/flows');
  const isFlowEditorRoute = location.pathname.startsWith('/flows/') && location.pathname !== '/flows';
  const isFlowListRoute = location.pathname === '/flows';
  const isSettingsRoute = location.pathname === '/settings';
  const isAuditRoute = location.pathname === '/admin/audit';
  const isLocalizationRoute = location.pathname === '/localization';
  const isCustomDataRoute = location.pathname === '/data/custom';
  const isDefaultDataRoute = location.pathname === '/data/default';
  const isInboxRoute = location.pathname.startsWith('/inbox');
  const isFunctionManagementRoute = location.pathname.startsWith('/function-management') || location.pathname.startsWith('/functions');
  const isAgentDeskOperationsRoute = location.pathname.startsWith('/agent-desk/operations') || location.pathname === '/agent-desk/monitor' || location.pathname === '/agent-desk/queues' || location.pathname === '/agent-desk/agents' || location.pathname === '/agent-desk/teams';
  const isAgentDeskConfigurationRoute = location.pathname.startsWith('/agent-desk/configuration') || location.pathname === '/agent-desk/canned-responses' || location.pathname === '/agent-desk/sla-policies' || location.pathname === '/agent-desk/custom-fields';
  const isAgentDeskRoute = location.pathname.startsWith('/agent-desk');
  const isChannelsRoute = location.pathname === '/channels';
  const isCampaignHubRoute = location.pathname === '/campaign-hub' || location.pathname.startsWith('/campaign-hub/');
  const isAnalyticsOverviewRoute = location.pathname === '/analytics';
  const isDeploymentsRoute = location.pathname === '/deployments';
  const isLeadsRoute = location.pathname === '/leads';
  const isConversationsRoute = location.pathname.startsWith('/analytics/conversations');
  /** Purpose: Select editor-specific header controls from the store. Why: The shared layout needs the current flow's available actions. */
  const headerActions = useFlowBuilderStore((state) => state.headerActions);
  /** Purpose: Select the current flow name. Why: The header must identify the graph being edited. */
  const currentFlowName = useFlowBuilderStore((state) => state.flowName);

  const adminMode = isAdmin();
  const hasBotAccess = Array.isArray(botAccess) && botAccess.length > 0;
  const enabledServices = getEnabledServices();
  const canUseVoice = adminMode || enabledServices.includes('voice');
  const canUseChat = adminMode || enabledServices.includes('chat');
  const canUseWhatsapp = adminMode || enabledServices.includes('whatsapp');
  const canUseChannels = adminMode || canUseVoice || canUseChat || canUseWhatsapp;
  const selectedBotAccess = botAccess.find((entry) => entry.botId === selectedBot);
  const selectedWorkspaceRoles = Array.isArray(selectedBotAccess?.workspaceRoles) && selectedBotAccess.workspaceRoles.length
    ? selectedBotAccess.workspaceRoles
    : [selectedBotAccess?.workspaceRole || ''];
  const canManageCampaignHub = adminMode || (canUseWhatsapp && (selectedWorkspaceRoles.includes('admin') || selectedWorkspaceRoles.includes('developer') || selectedBotAccess?.accessLevel === 'admin'));

  /** Purpose: Build navigation for the account role and workspace access. Why: Visible destinations should match the current account's capabilities. */
  const menuItems = useMemo(() => {
    const items = [];

    if (!adminMode) {
      if (!hasBotAccess) {
        items.push({
          text: 'Dashboard',
          icon: <DashboardOutlinedIcon fontSize="small" />,
          path: '/analytics',
          description: 'KPIs and trends',
          section: 'Overview',
        });
        items.push({
          text: 'Settings',
          icon: <SettingsIcon fontSize="small" />,
          path: '/settings',
          description: 'Workspace setup',
        });
        return items;
      }

    }

    items.push(
      {
        text: 'Dashboard',
        icon: <DashboardOutlinedIcon fontSize="small" />,
        path: '/analytics',
        description: 'KPIs and trends',
        section: 'Overview',
        exact: true,
      },
      {
        key: 'orchestration',
        text: 'Orchestration',
        icon: <AutoAwesomeIcon fontSize="small" />,
        description: 'Design, teach and release',
        section: 'Build & operate',
        children: [
          { text: 'Journey Studio', path: '/flows', icon: <AccountTreeIcon fontSize="small" />, visible: canUseChat || canUseWhatsapp || canUseVoice },
          { text: 'Functions', path: '/function-management', icon: <FunctionsIcon fontSize="small" />, visible: canUseChat || canUseWhatsapp || canUseVoice },
          { text: 'API Management', path: '/api-management', icon: <ApiIcon fontSize="small" />, visible: canUseChat || canUseWhatsapp || canUseVoice },
          { text: 'Knowledge Lab', path: '/train', icon: <SchoolIcon fontSize="small" />, visible: canUseChat || canUseWhatsapp },
          { text: 'Localization', path: '/localization', icon: <TranslateRoundedIcon fontSize="small" />, visible: canUseChat || canUseWhatsapp || canUseVoice },
          { text: 'Release Center', path: '/deployments', icon: <CloudUploadIcon fontSize="small" /> },
          { text: 'Operations Center', path: '/operations', icon: <DashboardOutlinedIcon fontSize="small" />, visible: adminMode },
        ].filter((item) => item.visible !== false),
      },
      {
        key: 'database',
        text: 'Database',
        icon: <StorageIcon fontSize="small" />,
        visible: canUseChat || canUseWhatsapp || canUseVoice,
        description: 'Platform and custom data',
        section: 'Data & intelligence',
        children: [
          { text: 'Default Table', path: '/data/default', icon: <DnsOutlinedIcon fontSize="small" /> },
          { text: 'Custom Data', path: '/data/custom', icon: <TableChartOutlinedIcon fontSize="small" /> },
        ],
      },
      {
        key: 'agent-desk',
        text: 'Agent Desk',
        icon: <InboxOutlinedIcon fontSize="small" />,
        visible: adminMode || hasBotAccess,
        description: 'Agent conversations',
        section: 'Agent Desk',
        children: [
          { text: 'Inbox Dashboard', path: '/inbox/dashboard', icon: <DashboardOutlinedIcon fontSize="small" /> },
          { text: 'Conversations', path: '/inbox/chats', icon: <ChatBubbleOutlineIcon fontSize="small" /> },
          { text: 'Operations', path: '/agent-desk/operations', icon: <SupportAgentOutlinedIcon fontSize="small" /> },
          { text: 'Configuration', path: '/agent-desk/configuration', icon: <SettingsIcon fontSize="small" /> },
        ],
      },
      { text: 'Calls', icon: <PhoneIcon fontSize="small" />, path: '/calls', visible: canUseVoice, description: 'Voice activity', section: 'Conversations' },
      { text: 'Channels', icon: <CampaignIcon fontSize="small" />, path: '/channels', visible: canUseChannels, description: 'Web, WhatsApp & Voice' },
      {
        key: 'campaign-hub',
        text: 'Campaign Hub',
        icon: <SendOutlinedIcon fontSize="small" />,
        description: 'Customer engagement',
        section: 'Customer Engagement',
        visible: canManageCampaignHub,
        children: [
          { text: 'Overview', path: '/campaign-hub', exact: true, icon: <DashboardOutlinedIcon fontSize="small" /> },
          { text: 'Message Templates', path: '/campaign-hub/templates', icon: <TemplateIcon fontSize="small" /> },
          { text: 'Campaigns', path: '/campaign-hub/campaigns', icon: <SendOutlinedIcon fontSize="small" /> },
          { text: 'Contacts & Audiences', key: 'campaign-contacts-audiences', icon: <ContactsIcon fontSize="small" />, children: [
            { text: 'Contacts', path: '/campaign-hub/contacts', icon: <ContactsIcon fontSize="small" /> },
            { text: 'Audiences', path: '/campaign-hub/audiences', icon: <PeopleIcon fontSize="small" /> },
          ] },
          { text: 'Analytics', path: '/campaign-hub/analytics', icon: <CampaignAnalyticsIcon fontSize="small" /> },
        ],
      },
      {
        key: 'customer-360',
        text: 'Customer 360°',
        icon: <Customer360Icon fontSize="small" />,
        description: 'Unified customer profiles',
        section: 'Customer Engagement',
        visible: canManageCampaignHub,
        children: [
          { text: 'All Customers', path: '/customer-360/customers', icon: <ContactsIcon fontSize="small" /> },
          { text: 'Consent Management', path: '/customer-360/consent', icon: <ConsentIcon fontSize="small" /> },
          { text: 'Activity Timeline', path: '/customer-360/timeline', icon: <TimelineIcon fontSize="small" /> },
        ],
      },
      { text: 'Leads', icon: <PeopleIcon fontSize="small" />, path: '/leads', description: 'Captured contacts', section: 'Insights' },
      { text: 'Conversations', icon: <ForumOutlinedIcon fontSize="small" />, path: '/analytics/conversations', description: 'Users, transcripts & logs' },
      { text: 'Settings', icon: <SettingsIcon fontSize="small" />, path: '/settings', description: 'Workspace setup', section: 'Workspace' },
    );

    if (adminMode) {
      items.push(
        {
          text: 'Platform Control',
          icon: <AdminPanelSettingsIcon fontSize="small" />,
          path: '/platform-control?section=tenants',
          description: 'Tenants and billing',
          children: [
            { text: 'Tenants', path: '/platform-control?section=tenants' },
            { text: 'Plans & Pricing', path: '/platform-control?section=plans-pricing' },
            { text: 'Platform Audit Logs', path: '/platform-control?section=audit' },
            { text: 'AI Token Usage', path: '/platform-control?section=ai-usage' },
            { text: 'All Bots', path: '/bots' },
          ],
        },
      );
    }

    return items.filter((item) => item.visible !== false);
  }, [adminMode, canManageCampaignHub, canUseChat, canUseVoice, canUseWhatsapp, hasBotAccess]);

  /** Purpose: Clear authentication and navigate to sign-in. Why: Workspace controls must stop using the previous authenticated session. */
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="workspace-shell min-h-screen bg-[radial-gradient(circle_at_top_left,rgba(56,189,248,0.18),transparent_24%),radial-gradient(circle_at_top_right,rgba(99,102,241,0.12),transparent_20%),linear-gradient(180deg,theme(colors.slate.50)_0%,theme(colors.slate.100)_100%)] text-slate-900 dark:text-slate-100 lg:h-screen lg:overflow-hidden">
      <div className="flex min-h-screen lg:h-screen lg:min-h-0">
        <div className="hidden shrink-0 lg:block lg:h-screen">
          <SidebarNav
            items={menuItems}
            collapsed={navCollapsed}
            pathname={`${location.pathname}${location.search || ''}`}
            onNavigate={(path) => navigate(path)}
            onToggleCollapse={() => setNavCollapsed((value) => !value)}
            brand="ZelorichBot"
            expandedWidthClass="w-[240px]"
          />
        </div>

        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-slate-950/60 backdrop-blur-sm lg:hidden"
              onClick={() => setMobileOpen(false)}
            >
              <motion.div
                initial={{ x: -320 }}
                animate={{ x: 0 }}
                exit={{ x: -320 }}
                transition={{ type: 'spring', stiffness: 260, damping: 30 }}
                className="h-full w-[84vw] max-w-[320px]"
                onClick={(event) => event.stopPropagation()}
              >
                <SidebarNav
                  items={menuItems}
                  collapsed={false}
                  pathname={`${location.pathname}${location.search || ''}`}
                  onNavigate={(path) => {
                    navigate(path);
                    setMobileOpen(false);
                  }}
                  brand="ZelorichBot"
                  footer={
                    <div className="space-y-3">
                      <SaaSButton variant="secondary" className="w-full justify-center" onClick={toggleMode}>
                        {mode === 'dark' ? <SunIcon fontSize="small" /> : <MoonIcon fontSize="small" />}
                        {mode === 'dark' ? 'Light mode' : 'Dark mode'}
                      </SaaSButton>
                      <SaaSButton variant="ghost" className="w-full justify-center" onClick={() => setMobileOpen(false)}>
                        <CloseIcon fontSize="small" />
                        Close
                      </SaaSButton>
                    </div>
                  }
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className={['flex min-w-0 flex-1 flex-col h-screen', isInboxRoute ? 'overflow-hidden' : 'lg:overflow-y-auto'].join(' ')}>
          <header className="workspace-header shrink-0 sticky top-0 z-30 border-b border-slate-200/80 bg-white/80 backdrop-blur-xl dark:border-white/10">
            <div className="flex items-center gap-2.5 px-2 py-2 sm:px-3 lg:px-4">
              <IconButton
                  onClick={() => setMobileOpen(true)}
                className="!text-slate-700 dark:!text-slate-100 lg:!hidden"
              >
                <MenuIcon />
              </IconButton>

              <div className="flex min-w-0 flex-1 items-center gap-2">
                {selectedBot && <EnvironmentSwitcher />}
                {isCampaignHubRoute && <div id="campaign-hub-page-header" className="flex min-w-0 flex-1 items-center" />}
                {isDeploymentsRoute && (
                  <div id="deployments-header-actions" className="flex min-w-0 flex-1 items-center gap-2 overflow-x-auto py-0.5 no-scrollbar" />
                )}
                {isAnalyticsOverviewRoute && (
                  <div className="flex min-w-0 flex-1 items-center gap-3 overflow-x-auto py-0.5 no-scrollbar">
                    <div className="shrink-0 truncate text-base font-extrabold tracking-[-0.025em] text-slate-950 dark:text-white">
                      Analytics overview
                    </div>
                    <div id="analytics-header-actions" className="flex min-w-0 flex-1 items-center gap-2 overflow-x-auto py-2 no-scrollbar" />
                  </div>
                )}
                {isFlowListRoute && (
                  <div id="flows-header-actions" className="flex items-center gap-2 min-w-0 flex-1 overflow-x-auto py-0.5" />
                )}
                {isFunctionManagementRoute && (
                  <div id="function-management-header-actions" className="flex items-center gap-2 min-w-0" />
                )}
                {isCustomDataRoute && (
                  <div id="custom-data-header-actions" className="flex items-center gap-2 min-w-0 flex-1 overflow-x-auto py-2 no-scrollbar" />
                )}
                {isInboxRoute && (
                  <div id="inbox-header-actions" className="flex items-center gap-2 min-w-0 flex-1 overflow-x-auto py-0.5 no-scrollbar" />
                )}
                {isFlowEditorRoute && selectedBot && (
                  <div className="flex shrink-0 items-center gap-1.5 rounded-xl border border-slate-200/90 bg-white/95 px-2.5 py-1.5 shadow-sm dark:border-white/10 dark:bg-slate-900/90">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 leading-none shrink-0 select-none">
                      Flow:
                    </span>
                    <span
                      className="truncate text-xs font-bold text-slate-800 dark:text-slate-100 max-w-[120px] sm:max-w-[160px] md:max-w-[220px]"
                      title={headerActions?.flowName || currentFlowName || 'Untitled flow'}
                    >
                      {headerActions?.flowName || currentFlowName || 'Untitled flow'}
                    </span>
                  </div>
                )}
                {isLocalizationRoute && (
                  <div id="localization-header-actions" className="flex items-center gap-1.5 min-w-0 flex-1 overflow-x-auto py-0.5 no-scrollbar" />
                )}
                {isDefaultDataRoute && (
                  <div className="flex min-w-0 flex-1 items-center gap-2.5 overflow-x-auto py-2 no-scrollbar">
                    <div id="default-data-header-actions" className="flex items-center gap-1.5 min-w-0 flex-1 overflow-x-auto py-1 no-scrollbar" />
                  </div>
                )}
                {isAgentDeskRoute && (
                  <div className="flex shrink-0 items-center gap-2 py-1">
                    <div className="truncate text-base font-extrabold tracking-[-0.02em] text-slate-950 dark:text-white">
                      {isAgentDeskConfigurationRoute ? 'Configuration' : isAgentDeskOperationsRoute ? 'Operations' : 'Agent Desk'}
                    </div>
                  </div>
                )}
                {isChannelsRoute && (
                  <div className="flex min-w-0 flex-1 items-center gap-2 py-1">
                    <div className="truncate text-base font-extrabold tracking-[-0.02em] text-slate-950 dark:text-white">
                      Channels
                    </div>
                    {selectedBot && (
                      <span className="truncate rounded-md bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                        {selectedBot}
                      </span>
                    )}
                    <div id="channels-header-actions" className="flex items-center gap-1.5 ml-auto" />
                  </div>
                )}
                {isLeadsRoute && (
                  <div className="flex shrink-0 items-center gap-2 py-1">
                    <div className="truncate text-base font-extrabold tracking-[-0.02em] text-slate-950 dark:text-white">
                      Leads
                    </div>
                  </div>
                )}
                {isConversationsRoute && (
                  <div className="flex shrink-0 items-center gap-2 py-1">
                    <div className="truncate text-base font-extrabold tracking-[-0.02em] text-slate-950 dark:text-white">
                      Conversation Logs
                    </div>
                  </div>
                )}
                {(isSettingsRoute || isAuditRoute) ? (
                  <div className="hidden min-w-0 flex-1 items-center justify-between gap-4 xl:flex">
                    <div className="min-w-0 max-w-[540px]">
                      <div className="truncate text-[2rem] font-extrabold tracking-[-0.04em] text-slate-950 dark:text-white">
                        {isAuditRoute ? 'Platform Audit Trail' : 'Settings'}
                      </div>
                      <div className="mt-0.5 truncate text-[13px] text-slate-600 dark:text-slate-300">
                        {isAuditRoute
                          ? 'Security-critical logs, tenant state changes, and administrative actions.'
                          : 'Workspace preferences, API access, integrations, publishing, and team controls in one place.'}
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2 rounded-full border border-slate-200 bg-white px-2.5 py-1.5 shadow-sm dark:border-white/10 dark:bg-slate-900/90">
                      <div className="leading-tight">
                        <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                          Appearance
                        </div>
                        <div className="text-[11px] text-slate-500 dark:text-slate-400">
                          Theme
                        </div>
                      </div>
                      <SaaSButton className="px-3 py-2 text-sm" onClick={toggleMode}>
                        {mode === 'dark' ? <SunIcon fontSize="small" /> : <MoonIcon fontSize="small" />}
                        {mode === 'dark' ? 'Light mode' : 'Dark mode'}
                      </SaaSButton>
                    </div>
                  </div>
                ) : null}
                {isFlowEditorRoute && selectedBot && (
                  <div className="hidden min-w-0 flex-1 items-center overflow-x-auto lg:flex">
                    {headerActions ? (
                      <div className="ml-auto flex shrink-0 items-center gap-1.5">
                        {headerActions.items?.map((item) => (
                          <button
                            key={item.key}
                            type="button"
                            onClick={item.onClick}
                            disabled={item.disabled}
                            className={item.className}
                          >
                            {item.icon}
                            <span>{item.label}</span>
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                )}
                {isInboxRoute && selectedBot ? (
                  <div id="inbox-header-navigation" className="hidden shrink-0 items-center justify-end xl:flex ml-auto mr-1" />
                ) : null}
              </div>
              {user && (
                <div className="ml-auto flex shrink-0 items-center gap-2 pl-1">
                  <div className="hidden text-right sm:block">
                    <div className="text-xs font-semibold text-slate-800 dark:text-slate-100">{user.name || user.email}</div>
                  </div>
                  {isAdmin() && (
                    <span className="rounded-full border border-cyan-400/20 bg-cyan-500/10 px-2.5 py-0.5 text-[10px] font-bold text-cyan-700 dark:text-cyan-200">
                      ADMIN
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="inline-flex items-center justify-center rounded-xl border border-slate-200/90 bg-white/95 p-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-rose-300 hover:bg-rose-50/50 hover:text-rose-600 dark:border-white/10 dark:bg-slate-900/90 dark:text-slate-200 dark:hover:border-rose-500/40 dark:hover:text-rose-300 cursor-pointer"
                    title="Logout"
                    aria-label="Logout"
                  >
                    <LogoutIcon sx={{ fontSize: 16 }} />
                  </button>
                </div>
              )}
            </div>

          </header>

          <main
            className={[
              isFlowEditorRoute
                ? 'h-[calc(100vh-64px)] overflow-hidden p-1 sm:p-1.5 lg:p-2'
                : isFunctionManagementRoute
                  ? 'h-[calc(100vh-58px)] overflow-hidden p-1 sm:p-1.5 lg:p-2'
                  : isInboxRoute
                    ? 'flex-1 min-h-0 overflow-hidden p-1 sm:p-1.5 lg:p-2 flex flex-col'
                    : isCustomDataRoute
                      ? 'min-h-[calc(100vh-64px)] p-1 sm:p-1.5 lg:p-2'
                    : isFlowListRoute
                      ? 'min-h-[calc(100vh-64px)] p-1 sm:p-1.5 lg:p-2'
                      : 'min-h-[calc(100vh-64px)] p-1 sm:p-1.5 lg:p-2',
            ].join(' ')}
          >
            {children}
          </main>
        </div>
      </div>
      <GlobalTicketNotification />
    </div>
  );
}
